import type { QueueTone } from '@/components/designSystem/QueueViews/types'
import { formatEligibilityReasons } from '@/constants/payment-eligibility.constants'
import type { PriorityPaymentRow } from '@/Network/Types/Responses/PriorityPaymentsResponses'
import type { PaymentModality } from '@/types/payment-eligibility.types'

/**
 * Frente B §6 — badge de elegibilidade na fila. Só as modalidades que o plantão TEM
 * (`types`): mostrar "Antecipe: inelegível" num plantão só À vista seria ruído.
 * `row.eligibility` ausente/`null` (ou a modalidade `null`) = não calculado → nenhum badge.
 */
export interface PriorityEligibilityBadge {
  modality: PaymentModality
  label: string
  tone: QueueTone
}

const hasInCash = (row: PriorityPaymentRow) => row.types.some((type) => type.startsWith('IN_CASH'))
const hasAntecipe = (row: PriorityPaymentRow) =>
  row.types.some((type) => type.startsWith('ANTECIPE'))

export function priorityEligibilityBadges(row: PriorityPaymentRow): PriorityEligibilityBadge[] {
  const eligibility = row.eligibility
  if (!eligibility) return []
  const badges: PriorityEligibilityBadge[] = []
  if (hasInCash(row) && typeof eligibility.inCashEligible === 'boolean') {
    badges.push(
      eligibility.inCashEligible
        ? { modality: 'IN_CASH', label: 'À vista: elegível', tone: 'ok' }
        : { modality: 'IN_CASH', label: 'À vista: Inelegível', tone: 'crit' }
    )
  }
  if (hasAntecipe(row) && typeof eligibility.antecipeEligible === 'boolean') {
    badges.push(
      eligibility.antecipeEligible
        ? { modality: 'ANTECIPE', label: 'Antecipe: elegível', tone: 'ok' }
        : { modality: 'ANTECIPE', label: 'Antecipe: Inelegível', tone: 'crit' }
    )
  }
  return badges
}

/** Motivos só das modalidades inelegíveis que o plantão tem (formatados de `reasons`). */
export function priorityEligibilityReasonLines(row: PriorityPaymentRow): string[] {
  const ineligible = priorityEligibilityBadges(row)
    .filter((badge) => badge.tone === 'crit')
    .map((badge) => badge.modality)
  if (!ineligible.length) return []
  return formatEligibilityReasons(row.eligibility?.reasons ?? [], ineligible)
}

export const formatPriorityEligibilityCell = (row: PriorityPaymentRow): string =>
  priorityEligibilityBadges(row)
    .map((badge) => badge.label)
    .join(' · ') || '—'
